/**
 * Cost Modeling & TCO Utilities
 */

import { CostMetadata, CostScenario, Currency, ExtendedArchiMateDiagram } from './extended-types';
import { ArchiMateElement } from './types';

export function calculateTCO(cost: CostMetadata): number {
  const lifespanYears = cost.lifespanYears || 3;
  const monthlyRecurring = cost.infrastructure + cost.licensing + cost.team + cost.maintenance + cost.other;
  const yearlyRecurring = monthlyRecurring * 12;
  const implementation = cost.implementationCost || 0;
  return implementation + yearlyRecurring * lifespanYears;
}

export function calculateElementCost(
  diagram: ExtendedArchiMateDiagram,
  elementId: string
): number {
  const metadata = diagram.elementMetadata[elementId];
  if (!metadata?.cost) return 0;
  return calculateTCO(metadata.cost);
}

export function calculateDiagramTotalCost(diagram: ExtendedArchiMateDiagram): {
  monthly: number;
  annual: number;
  tco: number;
  currency: Currency;
} {
  let monthly = 0;
  let annual = 0;
  let tco = 0;
  let currency: Currency = 'USD';

  diagram.elements.forEach((element) => {
    const metadata = diagram.elementMetadata[element.id];
    if (metadata?.cost) {
      const cost = metadata.cost;
      currency = cost.currency; // Use first currency found
      const monthlyRecurring =
        cost.infrastructure + cost.licensing + cost.team + cost.maintenance + cost.other;
      monthly += monthlyRecurring;
      annual += monthlyRecurring * 12;
      tco += calculateTCO(cost);
    }
  });

  return { monthly, annual, tco, currency };
}

export function getCostByLayer(diagram: ExtendedArchiMateDiagram): Record<string, number> {
  const costByLayer: Record<string, number> = {};

  diagram.elements.forEach((element) => {
    const metadata = diagram.elementMetadata[element.id];
    if (metadata?.cost) {
      const layer = element.layer;
      if (!costByLayer[layer]) costByLayer[layer] = 0;
      costByLayer[layer] += calculateTCO(metadata.cost);
    }
  });

  return costByLayer;
}

export function getCostByElementType(diagram: ExtendedArchiMateDiagram): Record<string, number> {
  const costByType: Record<string, number> = {};

  diagram.elements.forEach((element) => {
    const metadata = diagram.elementMetadata[element.id];
    if (metadata?.cost) {
      const type = element.type;
      if (!costByType[type]) costByType[type] = 0;
      costByType[type] += calculateTCO(metadata.cost);
    }
  });

  return costByType;
}

export function getTopCostElements(
  diagram: ExtendedArchiMateDiagram,
  limit: number = 10
): Array<{ element: ArchiMateElement; cost: number }> {
  const elementsWithCost: Array<{ element: ArchiMateElement; cost: number }> = [];

  diagram.elements.forEach((element) => {
    const cost = calculateElementCost(diagram, element.id);
    if (cost > 0) {
      elementsWithCost.push({ element, cost });
    }
  });

  return elementsWithCost.sort((a, b) => b.cost - a.cost).slice(0, limit);
}

export function createCostScenario(
  name: string,
  description: string,
  diagram: ExtendedArchiMateDiagram
): CostScenario {
  const costs = calculateDiagramTotalCost(diagram);

  const elementCosts: Record<string, CostMetadata> = {};
  diagram.elements.forEach((element) => {
    const metadata = diagram.elementMetadata[element.id];
    if (metadata?.cost) {
      elementCosts[element.id] = metadata.cost;
    }
  });

  return {
    id: `scenario-${Date.now()}`,
    name,
    description,
    elements: elementCosts,
    totalMonthlyCost: costs.monthly,
    totalAnnualCost: costs.annual,
    totalTCO: costs.tco,
    currency: costs.currency,
  };
}

export function compareCostScenarios(
  scenario1: CostScenario,
  scenario2: CostScenario
): {
  monthlySavings: number;
  annualSavings: number;
  tcoSavings: number;
  percentageSavings: number;
  winner: string;
} {
  const monthlySavings = scenario1.totalMonthlyCost - scenario2.totalMonthlyCost;
  const annualSavings = scenario1.totalAnnualCost - scenario2.totalAnnualCost;
  const tcoSavings = scenario1.totalTCO - scenario2.totalTCO;
  const percentageSavings = (tcoSavings / scenario1.totalTCO) * 100;

  return {
    monthlySavings,
    annualSavings,
    tcoSavings,
    percentageSavings,
    winner: tcoSavings > 0 ? scenario2.name : scenario1.name,
  };
}

export function getCostHeatmapData(diagram: ExtendedArchiMateDiagram): Record<string, number> {
  const heatmap: Record<string, number> = {};

  diagram.elements.forEach((element) => {
    const cost = calculateElementCost(diagram, element.id);
    heatmap[element.id] = cost;
  });

  return heatmap;
}

export function getCostColor(cost: number, maxCost: number): string {
  if (maxCost === 0) return '#10b981'; // green
  const ratio = cost / maxCost;

  if (ratio < 0.2) return '#10b981'; // green
  if (ratio < 0.4) return '#84cc16'; // lime
  if (ratio < 0.6) return '#eab308'; // yellow
  if (ratio < 0.8) return '#f97316'; // orange
  return '#ef4444'; // red
}

export function formatCurrency(amount: number, currency: Currency): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount);
}

export function estimateMigrationCost(
  currentDiagram: ExtendedArchiMateDiagram,
  targetDiagram: ExtendedArchiMateDiagram
): {
  currentTCO: number;
  targetTCO: number;
  migrationCost: number;
  savingsOverTime: number;
  breakEvenMonths: number;
  roiPercentage: number;
} {
  const current = calculateDiagramTotalCost(currentDiagram);
  const target = calculateDiagramTotalCost(targetDiagram);

  // Estimate migration cost as 20% of current TCO (configurable)
  const migrationCost = current.tco * 0.2;

  const monthlySavings = current.monthly - target.monthly;
  const savingsOverTime = monthlySavings * 36; // 3 years

  const breakEvenMonths = monthlySavings > 0 ? migrationCost / monthlySavings : Infinity;
  const roiPercentage =
    migrationCost > 0 ? ((savingsOverTime - migrationCost) / migrationCost) * 100 : 0;

  return {
    currentTCO: current.tco,
    targetTCO: target.tco,
    migrationCost,
    savingsOverTime,
    breakEvenMonths,
    roiPercentage,
  };
}
